/**
 * Modern Button Component System
 * Louis Nguyen Personal Brand
 */

import React from 'react';
import PropTypes from 'prop-types';
import './Button.css';

const Button = ({
  variant = 'primary',
  size = 'md',
  children,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseClass = 'btn-modern';
  const variantClass = `btn-modern--${variant}`;
  const sizeClass = `btn-modern--${size}`;
  const stateClasses = [
    disabled && 'btn-modern--disabled',
    loading && 'btn-modern--loading',
    fullWidth && 'btn-modern--full-width'
  ].filter(Boolean).join(' ');

  const buttonClasses = [
    baseClass,
    variantClass,
    sizeClass,
    stateClasses,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="btn-modern__spinner" aria-hidden="true">
          <svg viewBox="0 0 24 24" className="btn-modern__spinner-icon">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              strokeDasharray="32"
              strokeDashoffset="32"
            />
          </svg>
        </span>
      )}

      {leftIcon && !loading && (
        <span className="btn-modern__icon btn-modern__icon--left">
          {leftIcon}
        </span>
      )}

      <span className={`btn-modern__content ${loading ? 'btn-modern__content--loading' : ''}`}>
        {children}
      </span>

      {rightIcon && !loading && (
        <span className="btn-modern__icon btn-modern__icon--right">
          {rightIcon}
        </span>
      )}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'outline',
    'ghost',
    'destructive'
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
};

export default Button;
